import React, { useEffect, useState } from "react";
import { MapPin, PartyPopper, Rocket, User2 } from "lucide-react";
import { useTabContext } from "Common/Components/Tab/TabContext";
import Tab from "Common/Components/Tab/Tab";
import LoanApplicationForm from "../LoanApplicationForm";
import PatrimonialStateForm from "../PatrimonialStateForm";
import WarrantyForm from "../WarrantyForm";
import Completed from "../Completed";

const LoanApplicationModalContent = ({
  activeTab,
  setActiveTab,
  tabOrder,
  loanApplicationFormRef,
  patrimonialStateFormRef,
  warrantyFormRef,
  completedFormRef,
  onResetTab,
  closeToggle,
  isEdit,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  tabOrder: string[];
  loanApplicationFormRef: React.RefObject<any>;
  patrimonialStateFormRef: React.RefObject<any>;
  warrantyFormRef: React.RefObject<any>;
  completedFormRef: React.RefObject<any>;
  closeToggle: () => void;
  isEdit: boolean;
  onResetTab?: (fn: () => void) => void;
}) => {
  const { changeTab } = useTabContext();
  const [loanApplicationId, setLoanApplicationId] = useState<string | undefined>(undefined);

  const goToNextTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    const nextTab = tabOrder[currentIndex + 1];
    if (nextTab) {
      setActiveTab(nextTab);
      changeTab(nextTab);
    }
  };

  useEffect(() => {
    if (onResetTab) {
      onResetTab(() => {
        setLoanApplicationId(undefined);
        setActiveTab("loanApplicationForm");
        changeTab("loanApplicationForm");
      });
    }
  }, [onResetTab, setActiveTab, changeTab]);

  return (
    <>
      {/* TAB HEADER NO CLICKEABLE */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-6 nav-tabs form-wizard">
        {[
          { key: "loanApplicationForm", label: "1. Solicitud de Prestamo", icon: <User2 className="block h-6 mx-auto" /> },
          { key: "patrimonialStateForm", label: "2. Estado Patrimonial", icon: <MapPin className="block h-6 mx-auto" /> },
          { key: "warrantyForm", label: "3. Garantías", icon: <Rocket className="block h-6 mx-auto" /> },
          { key: "completed", label: "4. Completado", icon: <PartyPopper className="block h-6 mx-auto" /> },
        ].map(({ key, label, icon }) => (
          <div key={key} className="group/item">
            <div
              className={`block w-full px-4 py-5 text-center rounded-md ${
                activeTab === key
                  ? "text-custom-50 bg-custom-500"
                  : "text-slate-500 bg-slate-50 dark:text-zink-200 dark:bg-zink-600"
              }`}
            >
              {icon}
              <span className="block mt-3 font-medium text-15">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* CONTENIDO DEL FORMULARIO */}
      <Tab.Content className="mt-5 tab-content">
        <Tab.Pane eventKey="loanApplicationForm">
          <LoanApplicationForm
            ref={loanApplicationFormRef}
            isEdit={isEdit}
            onSuccess={(id: string) => {
              setLoanApplicationId(id);
              goToNextTab();
            }}
          />
        </Tab.Pane>

        <Tab.Pane eventKey="patrimonialStateForm">
          <PatrimonialStateForm
            ref={patrimonialStateFormRef}
            loanApplicationId={loanApplicationId}
            isEdit={isEdit}
            onSuccess={goToNextTab}
          />
        </Tab.Pane>

        <Tab.Pane eventKey="warrantyForm">
          <WarrantyForm
            ref={warrantyFormRef}
            loanApplicationId={loanApplicationId}
            isEdit={isEdit}
            onSuccess={goToNextTab}
          />
        </Tab.Pane>

        <Tab.Pane eventKey="completed">
          <Completed ref={completedFormRef} onCloseModal={closeToggle} />
        </Tab.Pane>
      </Tab.Content>
    </>
  );
};

export default LoanApplicationModalContent;
